import React, { useState } from 'react';
import { CheckIcon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { TIERS, FEATURES, FEATURE_CATEGORIES } from '../../constants/pricingConfig';

const TIER_IDS = ['starter', 'growth', 'pro', 'enterprise'];

const CellValue = ({ value }) => {
  if (typeof value === 'boolean') {
    return value ? (
      <CheckIcon className="w-5 h-5 text-lavender-600 mx-auto" aria-hidden="true" />
    ) : (
      <XMarkIcon className="w-5 h-5 text-gray-300 mx-auto" aria-hidden="true" />
    );
  }
  return <span className="text-sm font-medium text-gray-700">{value}</span>;
};

const CategoryGroup = ({ category, categoryFeatures, expandedCategory, onToggle }) => {
  const isExpanded = expandedCategory === category;

  return (
    <>
      <tr>
        <td colSpan={5} className="p-0">
          <button
            type="button"
            onClick={() => onToggle(category)}
            className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 border-y border-gray-100 text-left lg:pointer-events-none"
            aria-expanded={isExpanded}
          >
            <span className="text-xs font-bold uppercase tracking-wider text-gray-500">{category}</span>
            <ChevronDownIcon
              className={`w-4 h-4 text-gray-400 transition-transform lg:hidden ${isExpanded ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
          </button>
        </td>
      </tr>

      {categoryFeatures.map(feature => (
        <tr
          key={feature.key}
          className={`border-b border-gray-100 transition-all ${!isExpanded ? 'hidden lg:table-row' : ''}`}
        >
          <td className="py-3.5 px-4 text-sm text-gray-600 font-medium">{feature.label}</td>
          {TIER_IDS.map(tierId => {
            const value = feature[tierId];
            return (
              <td key={tierId} className={`py-3.5 px-4 text-center ${tierId === 'growth' ? 'bg-lavender-50/30' : ''}`}>
                <CellValue value={value} />
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
};

const FeatureComparisonSection = () => {
  const [expandedCategory, setExpandedCategory] = useState('Sales Channels');

  const toggleCategory = cat => {
    setExpandedCategory(prev => (prev === cat ? null : cat));
  };

  return (
    <section className="py-16 md:py-24 bg-gray-50/50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">Compare every feature</h2>
          <p className="text-gray-500 text-lg max-w-xl mx-auto">
            See exactly what you get at each tier. No hidden limits, no surprises.
          </p>
        </div>

        <div className="overflow-x-auto -mx-4 px-4 lg:mx-0 lg:px-0 bg-white rounded-2xl border border-gray-200 shadow-xs">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th scope="col" className="py-4 px-4 text-left text-sm font-semibold text-gray-900 w-[40%]">
                  Feature
                </th>
                {TIERS.map(tier => (
                  <th
                    key={tier.id}
                    scope="col"
                    className={`py-4 px-4 text-center text-sm font-semibold text-gray-900 w-[15%] ${
                      tier.highlight ? 'bg-lavender-50/30' : ''
                    }`}
                  >
                    {tier.name}
                    {tier.highlight && (
                      <span className="block text-xs font-medium text-lavender-600 mt-0.5">Popular</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {FEATURE_CATEGORIES.map(category => {
                const categoryFeatures = FEATURES.filter(f => f.category === category);
                if (categoryFeatures.length === 0) return null;
                return (
                  <CategoryGroup
                    key={category}
                    category={category}
                    categoryFeatures={categoryFeatures}
                    expandedCategory={expandedCategory}
                    onToggle={toggleCategory}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default FeatureComparisonSection;
